import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { warmUrlsForSync, extractMissionAssetUrls } from "../field_offline_resources"
import { observedFetch, currentLevel } from "../field_network_health"
import { timeoutFor } from "../field_timeout_policy"
import { logEvent, logError } from "../field_logger"
import { logSpeedEvent, FULL_SYNC_COMPLETE } from "../lib/field_speed_events"
import { upsertCachedPlace, upsertCachedResponse, expandSharedSurveys, runSingleFlight } from "../field_sync_shared"
import { warmInBatches } from "../field_warm_queue"
import { connectionLabel } from "../field_mission_download"

// Dedicated sync page controller.
// Runs a full two-phase sync: upload pending data, then download fresh missions.
// Auto-triggers on connect. Provides a "Sync now" retry button.
//
// Upload phase: dispatches field:online to trigger the field-sync controller,
// then polls IndexedDB until the pending queue drains (or times out).
//
// Download phase: fetches the prefetch API and upserts each response with
// per-item progress bar updates.

export default class extends Controller {
  static targets = [
    "syncButton", "statusText", "statusIcon", "lastSynced",
    "progressSection",
    "uploadBar",   "uploadLabel",   "uploadCount",
    "placesBar",   "placesLabel",   "placesCount",
    "missionsBar", "missionsLabel", "missionsCount",
    "assetsBar",   "assetsLabel",   "assetsCount",
    "warmBar",     "warmLabel",     "warmCount",
    "detailsSection", "detailsList",
    "emptyState"
  ]

  connect() {
    this._syncing = false
    this._updateLastSynced()
    this._restoreFromLocalStorage()
  }

  async sync() {
    if (this._syncing) return
    this._syncing = true
    this.syncButtonTarget.disabled = true

    try {
      // Card #4117 -- one full sync at a time across every instance of this
      // controller. The `_syncing` flag above only covers THIS instance, and
      // Turbo builds a fresh one each time the page is shown, so a tap on the
      // new page used to start a second sync on top of one still running
      // (two "Full sync started" 90 seconds apart on one phone).
      const outcome = await runSingleFlight("full-sync", () => this._fullSync())
      if (!outcome.ran) {
        this.statusTextTarget.textContent = "A sync is already running"
        logEvent("sync", "Full sync tap ignored, a sync is already running", { level: connectionLabel() }).catch(() => {})
      }
    } finally {
      this._syncing = false
      this.syncButtonTarget.disabled = false
    }
  }

  async _fullSync() {
    const startedAt = Date.now()
    this.emptyStateTarget.classList.add("hidden")
    this.progressSectionTarget.classList.remove("hidden")
    this.statusTextTarget.textContent = "Syncing..."
    logEvent("sync", "Full sync started", { level: connectionLabel() }).catch(() => {})

    try {
      await this._uploadPhase()
      const stats = await this._downloadPhase()
      // A sync that left missions off the phone is a warning, not a clean finish.
      const level = stats.downloaded < stats.assigned ? "warn" : "event"
      logSpeedEvent(FULL_SYNC_COMPLETE, {
        ...stats, durationMs: Date.now() - startedAt, level: connectionLabel()
      }, { level }).catch(() => {})
    } catch (error) {
      console.error("Sync failed:", error)
      // Manual, user-triggered sync with no automatic retry -- terminal for
      // this attempt (semantics table, docs/dev_patterns/pwa-diagnostics-telemetry.md).
      logError("sync", "Full sync failed", {
        message: error?.message || String(error), durationMs: Date.now() - startedAt, level: connectionLabel()
      }).catch(() => {})
      this._setStatusError()
    }
  }

  // -- private --

  async _uploadPhase() {
    const pendingResponses = await db.pending_responses.where("status").equals("pending").count()
    const pendingImages    = await db.pending_images.where("status").equals("pending").count()
    const total = pendingResponses + pendingImages

    if (total === 0) {
      this._setUploadBar(100, "Nothing to upload", "")
      return
    }

    this._setUploadBar(0, "Uploading...", `0 / ${total}`)

    // Trigger the field-sync controller (mounted in the header)
    // A tasker on the Sync page asked for this: clear the photo retry wait
    // too (card #3844).
    window.dispatchEvent(new CustomEvent("field:online", { detail: { userInitiated: true } }))

    // Poll until queue drains or 20s timeout
    await new Promise(resolve => {
      const deadline = Date.now() + 20000
      const poll = setInterval(async () => {
        const rem    = await db.pending_responses.where("status").equals("pending").count()
        const remImg = await db.pending_images.where("status").equals("pending").count()
        const done   = total - (rem + remImg)
        const pct    = Math.round((done / total) * 100)
        this._setUploadBar(pct, "Uploading...", `${done} / ${total}`)

        if ((rem + remImg) === 0 || Date.now() > deadline) {
          clearInterval(poll)
          const synced = total - (rem + remImg)
          this._setUploadBar(100, `${synced} uploaded`, "")
          resolve()
        }
      }, 300)
    })
  }

  async _downloadPhase() {
    this._setPlacesBar(0, "Waiting...", "")
    this._setMissionsBar(0, "Waiting...", "")

    const downloadStartedAt = Date.now()
    const { prefetchData, bytes } = await this._fetchPrefetch()
    const { responses, removed_ids, synced_at } = prefetchData

    // Handle tombstones
    for (const id of (removed_ids || [])) {
      const local = await db.cached_responses.where("responseId").equals(id).first()
      if (!local) continue
      const hasData = local.resultJson && Object.keys(local.resultJson).length > 0
      if (hasData) {
        await db.cached_responses.update(local.id, { status: "pending_upload" })
      } else {
        await db.cached_responses.delete(local.id)
      }
    }

    // ── Places phase ──────────────────────────────────────────────────────────
    const uniquePlaces = []
    const seenPlaceIds = new Set()
    for (const r of responses) {
      if (!r.place?.id || seenPlaceIds.has(r.place.id)) continue
      seenPlaceIds.add(r.place.id)
      uniquePlaces.push(r.place)
    }

    const placeTotal = uniquePlaces.length
    let placeDone = 0
    this._setPlacesBar(0, "Saving places...", placeTotal > 0 ? `0 / ${placeTotal}` : "")

    for (const place of uniquePlaces) {
      await upsertCachedPlace(db, place)
      placeDone++
      const pct = Math.round((placeDone / placeTotal) * 100)
      this._setPlacesBar(pct, "Saving places...", `${placeDone} / ${placeTotal}`)
    }

    const placesLabel = placeTotal === 0 ? "No places" : `${placeTotal} place${placeTotal === 1 ? "" : "s"} saved`
    this._setPlacesBar(100, placesLabel, "")

    // ── Missions phase ────────────────────────────────────────────────────────
    const total = responses.length
    let done = 0
    this._setMissionsBar(0, "Saving missions...", total > 0 ? `0 / ${total}` : "")

    // Shared helper (card #3804) merges any existing local draft over the
    // server's resultJson instead of overwriting it outright -- a full sync
    // must not clobber in-progress answers in an open editor tab.
    // Card #4117 -- one response failing to save must not stop the rest, and
    // the log must say how many actually landed. downloaded counts only
    // missions that came with their survey and were written to the phone.
    let downloaded = 0
    const failedIds = []
    for (const r of responses) {
      try {
        if (r.survey_json) {
          await upsertCachedResponse(db, r, synced_at)
          downloaded++
        } else {
          failedIds.push(r.id)
        }
      } catch {
        failedIds.push(r.id)
      }
      done++
      const pct = Math.round((done / total) * 100)
      this._setMissionsBar(pct, "Saving missions...", `${done} / ${total}`)
    }

    // Remove stale entries (preserve pending_upload records)
    const prefetchIds = new Set(responses.map(r => r.id))
    await db.cached_responses.filter(r =>
      !prefetchIds.has(r.responseId) && r.status !== "pending_upload"
    ).delete()

    localStorage.setItem("field:last-prefetch", synced_at)

    // Persist summary so connect() can restore synchronously on next page load
    const scheduled = responses.filter(r => r.schedule_at).length
    const inProgress = responses.filter(r => r.status === "started").length
    const badConn = responses.filter(r => r.place?.bad_connectivity).length
    const assetTotal = prefetchData.mission_assets?.total || 0
    localStorage.setItem("field:sync-summary", JSON.stringify({ total, scheduled, inProgress, badConn, assetTotal, downloaded }))
    window.dispatchEvent(new CustomEvent("field:mission-counts-changed"))

    this._updateLastSynced()

    const missionsLabel = total === 0 ? "No missions" : `${downloaded} of ${total} mission${total === 1 ? "" : "s"} saved`
    this._setMissionsBar(100, missionsLabel, "")
    this._setStatusSuccess(downloaded)

    // ── Assets phase ──────────────────────────────────────────────────────────
    await this._assetsPhase(prefetchData)

    this._showDetails(responses, prefetchData.mission_assets?.total || 0)

    // ── Warming phase ─────────────────────────────────────────────────────────
    const warm = await this._warmPhase(responses)

    return {
      assigned: total, downloaded, failed: failedIds.length,
      failedSample: failedIds.slice(0, 5),
      places: placeTotal, bytes,
      sharedSurveys: Object.keys(prefetchData.surveys || {}).length,
      downloadMs: Date.now() - downloadStartedAt,
      warmTotal: warm.total, warmCached: warm.cached, warmPending: warm.pending
    }
  }

  // The one big request. If it does not come back in time, ask once more with
  // twice the patience before calling the sync failed. dedupe_surveys=1 sends
  // each distinct survey once (card #4117), which is most of the payload for a
  // heavy user. Returns the parsed payload and its size in bytes.
  async _fetchPrefetch() {
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    let lastError = null

    for (const patience of [1, 2]) {
      try {
        const response = await observedFetch("/api/v1/field/prefetch.json?dedupe_surveys=1", {
          method: "GET",
          headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken },
          signal: AbortSignal.timeout(timeoutFor("prefetch", currentLevel()) * patience)
        }, { kind: "prefetch", sizeClass: "small" })

        if (!response.ok) throw new Error(`Server error: ${response.status}`)

        const text = await response.text()
        const prefetchData = expandSharedSurveys(JSON.parse(text))
        return { prefetchData, bytes: new Blob([text]).size }
      } catch (error) {
        lastError = error
        if (patience === 1) {
          logEvent("sync", "Full sync download retrying with a longer wait", {
            message: error?.message || String(error), level: connectionLabel()
          }).catch(() => {})
        }
      }
    }
    throw lastError
  }

  // Warms in small batches, most important pages first (today's and started
  // missions), and keeps what did not get cached for a later resume (card
  // #4117, field_warm_queue.js). Resolves with { total, cached, pending }.
  async _warmPhase(responses) {
    const sw = navigator.serviceWorker?.controller
    const urlsToWarm = warmUrlsForSync(responses)

    if (!sw || urlsToWarm.length === 0) {
      this._setWarmBar(100, "Nothing to cache", "")
      return { total: 0, cached: 0, pending: 0 }
    }

    this._setWarmBar(0, "Caching pages...", `0 / ${urlsToWarm.length}`)

    const result = await warmInBatches(urlsToWarm, {
      trigger: "full-sync",
      onProgress: ({ done, total }) => {
        this._setWarmBar(Math.round((done / total) * 100), "Caching pages...", `${done} / ${total}`)
      }
    })

    const label = result.pending > 0
      ? `${result.cached} of ${urlsToWarm.length} pages cached, the rest will follow`
      : `${urlsToWarm.length} page${urlsToWarm.length === 1 ? "" : "s"} cached`
    this._setWarmBar(100, label, "")
    return { total: urlsToWarm.length, cached: result.cached, pending: result.pending }
  }

  async _assetsPhase(prefetchData) {
    const sw   = navigator.serviceWorker?.controller
    const urls = extractMissionAssetUrls(prefetchData)

    if (!sw || urls.length === 0) {
      this._setAssetsBar(100, "No assets to cache", "")
      return
    }

    this._setAssetsBar(0, "Caching assets...", `0 / ${urls.length}`)

    await new Promise(resolve => {
      let cached = 0
      const channel = new MessageChannel()

      channel.port1.onmessage = () => {
        cached++
        const pct = Math.round((cached / urls.length) * 100)
        this._setAssetsBar(pct, "Caching assets...", `${cached} / ${urls.length}`)
        if (cached >= urls.length) resolve()
      }

      setTimeout(resolve, 30000)

      sw.postMessage({ type: "CACHE_ASSETS", urls }, [channel.port2])
    })

    this._setAssetsBar(100, `${urls.length} asset${urls.length === 1 ? "" : "s"} cached`, "")
  }

  _showDetails(responses, assetTotal = 0) {
    const scheduled    = responses.filter(r => r.schedule_at && !r.place?.bad_connectivity).length
    const badConn      = responses.filter(r => r.place?.bad_connectivity).length
    const inProgress   = responses.filter(r => r.status === "started").length

    const rows = [
      { label: "Scheduled missions",       count: scheduled,  icon: "calendar" },
      { label: "Poor signal places",       count: badConn,    icon: "signal" },
      { label: "Responses in progress",    count: inProgress, icon: "play" },
      { label: "Mission assets cached",    count: assetTotal, icon: "image" },
    ]

    const icons = {
      calendar: `<path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>`,
      signal:   `<path stroke-linecap="round" stroke-linejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/>`,
      play:     `<path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>`,
      image:    `<path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>`,
    }

    this.detailsListTarget.innerHTML = rows.map(({ label, count, icon }) => `
      <div class="px-4 py-2.5 flex items-center justify-between">
        <div class="flex items-center gap-2.5">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">${icons[icon]}</svg>
          <span class="text-sm text-slate-600">${label}</span>
        </div>
        <span class="text-sm font-semibold ${count > 0 ? "text-slate-800" : "text-slate-300"}">${count}</span>
      </div>
    `).join("")

    this.detailsSectionTarget.classList.remove("hidden")
  }

  _setUploadBar(pct, label, count) {
    this.uploadBarTarget.style.width    = `${pct}%`
    this.uploadLabelTarget.textContent  = label
    this.uploadCountTarget.textContent  = count
  }

  _setPlacesBar(pct, label, count) {
    this.placesBarTarget.style.width    = `${pct}%`
    this.placesLabelTarget.textContent  = label
    this.placesCountTarget.textContent  = count
  }

  _setMissionsBar(pct, label, count) {
    this.missionsBarTarget.style.width    = `${pct}%`
    this.missionsLabelTarget.textContent  = label
    this.missionsCountTarget.textContent  = count
  }

  _setAssetsBar(pct, label, count) {
    this.assetsBarTarget.style.width    = `${pct}%`
    this.assetsLabelTarget.textContent  = label
    this.assetsCountTarget.textContent  = count
  }

  _setWarmBar(pct, label, count) {
    this.warmBarTarget.style.width    = `${pct}%`
    this.warmLabelTarget.textContent  = label
    this.warmCountTarget.textContent  = count
  }

  _setStatusSuccess(downloadCount) {
    this.statusTextTarget.textContent = downloadCount === 0
      ? "All up to date"
      : `${downloadCount} mission${downloadCount === 1 ? "" : "s"} ready offline`
    this.statusIconTarget.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
      </svg>`
  }

  _setStatusError() {
    this.statusTextTarget.textContent = "Sync failed — check your connection"
    this.statusIconTarget.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
      </svg>`
  }

  _restoreFromLocalStorage() {
    const raw = localStorage.getItem("field:sync-summary")
    if (!raw) return

    let summary
    try { summary = JSON.parse(raw) } catch { return }

    const { total, scheduled, inProgress, badConn, assetTotal = 0 } = summary
    if (total == null) return

    this.emptyStateTarget.classList.add("hidden")
    this.progressSectionTarget.classList.remove("hidden")
    this._setUploadBar(100, "Nothing to upload", "")
    this._setPlacesBar(100, "Places saved", "")
    this._setMissionsBar(100, `${total} mission${total === 1 ? "" : "s"} saved`, "")
    this._setAssetsBar(100, assetTotal > 0 ? `${assetTotal} asset${assetTotal === 1 ? "" : "s"} cached` : "No assets to cache", "")
    this._setWarmBar(100, "Pages cached", "")
    this._setStatusSuccess(total)

    const rows = [
      { label: "Scheduled missions",    count: scheduled,   icon: "calendar" },
      { label: "Poor signal places",    count: badConn,     icon: "signal" },
      { label: "Responses in progress", count: inProgress,  icon: "play" },
      { label: "Mission assets cached", count: assetTotal,  icon: "image" },
    ]

    const icons = {
      calendar: `<path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>`,
      signal:   `<path stroke-linecap="round" stroke-linejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/>`,
      play:     `<path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>`,
      image:    `<path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>`,
    }

    this.detailsListTarget.innerHTML = rows.map(({ label, count, icon }) => `
      <div class="px-4 py-2.5 flex items-center justify-between">
        <div class="flex items-center gap-2.5">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">${icons[icon]}</svg>
          <span class="text-sm text-slate-600">${label}</span>
        </div>
        <span class="text-sm font-semibold ${count > 0 ? "text-slate-800" : "text-slate-300"}">${count}</span>
      </div>
    `).join("")

    this.detailsSectionTarget.classList.remove("hidden")
  }

  _updateLastSynced() {
    const lastSync = localStorage.getItem("field:last-prefetch")
    if (lastSync) {
      const ago = this._timeAgo(new Date(lastSync))
      this.lastSyncedTarget.textContent = `Last synced ${ago}`
    } else {
      this.lastSyncedTarget.textContent = "Not yet synced"
    }
  }

  _timeAgo(date) {
    const seconds = Math.floor((Date.now() - date) / 1000)
    if (seconds < 60)  return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60)  return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24)    return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }
}
